import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

const flagIcons = {
  en: '🇬🇧',
  pt: '🇵🇹',
  fr: '🇫🇷',
};

type Language = 'en' | 'pt' | 'fr';

const LanguageToggle = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentLanguage = i18n.language.substring(0, 2) as Language;
  const languages: Language[] = ['en', 'pt', 'fr'];

  const changeLanguage = (lng: Language) => {
    i18n.changeLanguage(lng);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="language-button px-2 py-2 rounded-md hover:bg-black/5"
        aria-label="Toggle language menu"
        aria-expanded={isOpen}
      >
        <span className="text-base">{flagIcons[currentLanguage]}</span>
        <span className="hidden sm:inline text-xs font-semibold">{t(`languages.${currentLanguage}`)}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16 }}
            className="language-dropdown absolute right-0 mt-2 w-44 py-1 z-50"
          >
            {languages.map((lng) => (
              <button
                type="button"
                key={lng}
                onClick={() => changeLanguage(lng)}
                className={`block w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 ${
                  currentLanguage === lng
                    ? 'text-[#111315] bg-black/5 font-semibold'
                    : 'text-[#555b62] hover:text-[#111315] hover:bg-black/5'
                }`}
              >
                <span className="mr-2">{flagIcons[lng]}</span>
                {t(`languages.${lng}`)}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageToggle;
